/**
 * Onboard User Page - Single and Bulk User Onboarding
 */
import { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
import { UserPlus, Download, FileSpreadsheet, AlertCircle, CheckCircle, Loader, Eye, EyeOff, X } from 'lucide-react';
import { bulkUserAPI, BulkUserItem, BulkUserUploadResponse } from '@/api/bulkUserAPI';
import { organizationAPI, Organization } from '@/api/organizationAPI';
import { roleAPI, RoleDTO } from '@/api/roleAPI';
import { userAPI } from '@/api/userAPI';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

interface FormErrors {
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  organization?: string;
}

interface ExcelRowError {
  row: number;
  email: string;
  error: string;
  data?: any;
}

export const OnboardUser = () => {
  // const navigate = useNavigate();
  
  // Single user form state
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  
  // Organizations list
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [roles, setRoles] = useState<RoleDTO[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  
  // Bulk upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUserUploadResponse | null>(null);
  const [excelErrors, setExcelErrors] = useState<ExcelRowError[]>([]);
  const [errorReportBlob, setErrorReportBlob] = useState<Blob | null>(null);
  
  // Fetch organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        const orgs = await organizationAPI.getOrganizations({ limit: 100 });
        setOrganizations(orgs);
      } catch (err: any) {
        toast.error('Failed to load organizations');
      } finally {
        setLoadingOrgs(false);
      }
    };
    fetchOrganizations();
    const fetchRoles = async () => {
      try {
        setLoadingRoles(true);
        const data = await roleAPI.getAllRoles();
        setRoles(data);
        if (data && data.length > 0) {
          setRoleId(data[0].id);
        }
      } catch {
        toast.error('Failed to load roles');
      } finally {
        setLoadingRoles(false);
      }
    };
    fetchRoles();
  }, []);
  
  // Client-side validation for single user
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!username.trim()) newErrors.username = 'Username is required';
    else if (username.length < 3) newErrors.username = 'Username must be at least 3 characters';
    else if (username.length > 50) newErrors.username = 'Username must be less than 50 characters';
    else if (!/^[a-zA-Z0-9_.\s]+$/.test(username))
      newErrors.username = 'Username can only contain letters, numbers, underscores, spaces, and dots';
    
    if (!firstName.trim()) newErrors.firstName = 'First name is required';
    else if (firstName.length < 2) newErrors.firstName = 'First name must be at least 2 characters';
    else if (firstName.length > 100) newErrors.firstName = 'First name must be less than 100 characters';
    else if (!/^[a-zA-Z\s'-]+$/.test(firstName)) newErrors.firstName = 'First name can only contain letters, spaces, hyphens, and apostrophes';
    
    if (!lastName.trim()) newErrors.lastName = 'Last name is required';
    else if (lastName.length < 2) newErrors.lastName = 'Last name must be at least 2 characters';
    else if (lastName.length > 100) newErrors.lastName = 'Last name must be less than 100 characters';
    else if (!/^[a-zA-Z\s'-]+$/.test(lastName)) newErrors.lastName = 'Last name can only contain letters, spaces, hyphens, and apostrophes';
    
    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Please enter a valid email address';
    
    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    else if (password.length > 72) newErrors.password = 'Password must be less than 72 characters';
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) newErrors.password = 'Password must contain uppercase, lowercase, and a number';
    
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSingleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    const payload: any = {
      username: username.trim(),
      email: email.trim().toLowerCase(),
      password,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    };
    if (roleId) {
      payload.role_id = roleId;
    }
    
    try {
      setLoading(true);
      await userAPI.createUser(payload);
      toast.success('User created successfully!');
      // Reset form
      setUsername('');
      setFirstName('');
      setLastName('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setOrganizationId('');
      setErrors({});
    } catch (err: any) {
      const errorMsg = err?.response?.data?.detail || err?.message || 'Failed to create user';
      toast.error(errorMsg);
      if (err?.field && err?.message) {
        setErrors(prev => ({ ...prev, [err.field]: err.message }));
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleInputChange = (field: keyof FormErrors, value: string) => {
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    switch (field) {
      case 'username': setUsername(value); break;
      case 'firstName': setFirstName(value); break;
      case 'lastName': setLastName(value); break;
      case 'email': setEmail(value); break;
      case 'password': setPassword(value); break;
      case 'confirmPassword': setConfirmPassword(value); break;
    }
  };
  
  const renderInput = (
    id: keyof FormErrors,
    label: string,
    value: string,
    type = 'text',
    placeholder = '',
    showToggle?: boolean,
    toggleValue?: boolean,
    onToggle?: () => void
  ) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          id={id}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => handleInputChange(id, e.target.value)}
          className={`input w-full pr-${showToggle ? '10' : '2'} ${errors[id] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
        />
        {showToggle && toggleValue !== undefined && onToggle && (
          <button 
            type="button" 
            tabIndex={-1}
            className="absolute inset-y-0 right-0 pr-3 flex items-center z-10" 
            onClick={onToggle}
            aria-label={toggleValue ? 'Hide password' : 'Show password'}
          >
            {toggleValue ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
          </button>
        )}
      </div>
      {errors[id] && <p className="mt-1 text-sm text-red-600">{errors[id]}</p>}
    </div>
  );
  
  // Generate and download template locally
  const handleDownloadTemplate = () => {
    try {
      // Create template data
      const templateData = [
        {
          username: 'john.doe',
          email: 'john.doe@example.com',
          first_name: 'John',
          last_name: 'Doe',
          password: 'SecurePass123!',
          role: 'developer',
          organization_id: '',
          display_name: '',
          bio: '',
          timezone: 'UTC',
          language: 'en'
        },
        {
          username: 'jane.smith',
          email: 'jane.smith@example.com',
          first_name: 'Jane',
          last_name: 'Smith',
          password: 'SecurePass123!',
          role: 'developer',
          organization_id: '',
          display_name: '',
          bio: '',
          timezone: 'UTC',
          language: 'en'
        }
      ];
      
      // Create workbook with two sheets
      const wb = XLSX.utils.book_new();
      
      // Users sheet
      const wsUsers = XLSX.utils.json_to_sheet(templateData);
      XLSX.utils.book_append_sheet(wb, wsUsers, 'Users');
      
      // Instructions sheet
      const instructions = [
        { Instructions: '1. Fill in the user details in the Users sheet' },
        { Instructions: '2. Required fields: username, email, first_name, last_name, password' },
        { Instructions: '3. Valid roles: super_admin, admin, manager, developer, viewer' },
        { Instructions: '4. Password must be at least 8 characters and contain uppercase, lowercase, and a number' },
        { Instructions: '5. Email must be unique' },
        { Instructions: '6. Username must be unique and at least 3 characters' },
        { Instructions: '7. Organization ID can be found in Organizations page (optional)' },
        { Instructions: '8. Remove the example rows before uploading' },
        { Instructions: '9. Maximum 100 users per upload' }
      ];
      const wsInstructions = XLSX.utils.json_to_sheet(instructions);
      XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');
      
      // Write to file
      XLSX.writeFile(wb, 'bulk_user_upload_template.xlsx');
      toast.success('Template downloaded successfully');
    } catch (err: any) {
      toast.error('Failed to generate template');
    }
  };
  
  // Validate Excel file
  const validateExcelFile = (data: any[]): ExcelRowError[] => {
    const errors: ExcelRowError[] = [];
    const requiredFields = ['username', 'email', 'first_name', 'last_name', 'password'];
    const seenEmails = new Set<string>();
    
    data.forEach((row, index) => {
      const rowNum = index + 2; // +2 because Excel rows start at 1 and we have header
      const rowErrors: string[] = [];
      const email = (row.email || '').toString().trim().toLowerCase();
      
      // Check required fields
      for (const field of requiredFields) {
        if (!row[field] || (typeof row[field] === 'string' && !row[field].trim())) {
          rowErrors.push(`Missing required field: ${field}`);
        }
      }
      
      // Email validation
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        rowErrors.push('Invalid email format');
      }
      
      // Check for duplicate emails in file
      if (email && seenEmails.has(email)) {
        rowErrors.push('Duplicate email in file');
      } else if (email) {
        seenEmails.add(email);
      }
      
      // Username validation
      if (row.username) {
        const username = row.username.toString().trim();
        if (username.length < 3) rowErrors.push('Username must be at least 3 characters');
        if (username.length > 50) rowErrors.push('Username must be less than 50 characters');
        if (!/^[a-zA-Z0-9_.\s]+$/.test(username)) rowErrors.push('Username contains invalid characters');
      }
      
      // Name validation
      if (row.first_name && row.first_name.toString().trim().length < 2) {
        rowErrors.push('First name must be at least 2 characters');
      }
      if (row.last_name && row.last_name.toString().trim().length < 2) {
        rowErrors.push('Last name must be at least 2 characters');
      }
      
      // Password validation
      if (row.password) {
        const password = row.password.toString();
        if (password.length < 8) rowErrors.push('Password must be at least 8 characters');
        if (password.length > 72) rowErrors.push('Password must be less than 72 characters');
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
          rowErrors.push('Password must contain uppercase, lowercase, and a number');
        }
      }
      
      if (rowErrors.length > 0) {
        errors.push({
          row: rowNum,
          email: email || 'N/A',
          error: rowErrors.join('; '),
          data: row
        });
      }
    });
    
    return errors;
  };
  
  // Handle file upload
  const handleFileUpload = async (file: File) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);
          
          if (jsonData.length === 0) {
            toast.error('Excel file is empty');
            return;
          }
          
          // Validate the file
          const validationErrors = validateExcelFile(jsonData as any[]);
          
          if (validationErrors.length > 0) {
            // Generate error report
            const errorReport = jsonData.map((row: any, index) => {
              const rowError = validationErrors.find(e => e.row === index + 2);
              return {
                ...row,
                Error: rowError ? rowError.error : ''
              };
            });
            
            const errorWs = XLSX.utils.json_to_sheet(errorReport);
            const errorWb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(errorWb, errorWs, 'Errors');
            const errorBlob = XLSX.write(errorWb, { type: 'array', bookType: 'xlsx' });
            setErrorReportBlob(new Blob([errorBlob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            setExcelErrors(validationErrors);
            toast.error(`${validationErrors.length} row(s) have validation errors. Please fix and try again.`);
            return;
          }
          
          // Convert to API format
          const users: BulkUserItem[] = (jsonData as any[]).map((row: any) => ({
            username: row.username?.toString().trim(),
            email: row.email?.toString().trim().toLowerCase(),
            first_name: row.first_name?.toString().trim(),
            last_name: row.last_name?.toString().trim(),
            password: row.password?.toString(),
            role: row.role?.toString().trim() || 'developer',
            organization_id: row.organization_id?.toString().trim() || undefined,
            display_name: row.display_name?.toString().trim() || undefined,
            bio: row.bio?.toString().trim() || undefined,
            timezone: row.timezone?.toString().trim() || 'UTC',
            language: row.language?.toString().trim() || 'en',
          }));
          
          // Upload to API
          setUploading(true);
          const result = await bulkUserAPI.createBulkUsers(users);
          setUploadResult(result);
          
          if (result.failed > 0) {
            // Generate error report for API errors
            const errorReport = jsonData.map((row: any, index) => {
              const apiError = result.errors.find(e => e.row === index + 2);
              return {
                ...row,
                Error: apiError ? apiError.error : ''
              };
            });
            
            const errorWs = XLSX.utils.json_to_sheet(errorReport);
            const errorWb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(errorWb, errorWs, 'Errors');
            const errorBlob = XLSX.write(errorWb, { type: 'array', bookType: 'xlsx' });
            setErrorReportBlob(new Blob([errorBlob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            setExcelErrors(result.errors.map(e => ({ ...e, data: {} })));
            toast(`${result.successful} user(s) created, ${result.failed} failed`, { icon: '⚠️' });
          } else {
            toast.success(`Successfully created ${result.successful} user(s)!`);
          }
        } catch (err: any) {
          toast.error('Failed to process Excel file: ' + (err.message || 'Unknown error'));
        } finally {
          setUploading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err: any) {
      toast.error('Failed to read file: ' + (err.message || 'Unknown error'));
    }
  };
  
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setUploadFile(file);
    setUploadResult(null);
    setExcelErrors([]);
    setErrorReportBlob(null);
    handleFileUpload(file);
  };
  
  const handleDownloadErrorReport = () => {
    if (!errorReportBlob) return;
    
    const url = window.URL.createObjectURL(errorReportBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_upload_errors.xlsx';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Onboard User</h1>
        <p className="text-gray-600 mt-1">Create single or multiple users at once</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Single User Form */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Single User Onboarding
          </h2>
          
          <form onSubmit={handleSingleUserSubmit} className="space-y-4">
            {renderInput('username', 'Username', username, 'text', 'johndoe')}
            
            <div className="grid grid-cols-2 gap-4">
              {renderInput('firstName', 'First Name', firstName, 'text', 'John')}
              {renderInput('lastName', 'Last Name', lastName, 'text', 'Doe')}
            </div>
            
            {renderInput('email', 'Email', email, 'email', 'your@email.com')}
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                id="role"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                className="input w-full"
                disabled={loadingRoles}
                required
              >
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.display_name || r.name}</option>
                ))}
              </select>
              {roles.length === 0 && !loadingRoles && (
                <p className="mt-1 text-xs text-gray-500">No roles available</p>
              )}
            </div>
            
            {renderInput('password', 'Password', password, showPassword ? 'text' : 'password', '••••••••', true, showPassword, () => setShowPassword(!showPassword))}
            {renderInput('confirmPassword', 'Confirm Password', confirmPassword, showConfirmPassword ? 'text' : 'password', '••••••••', true, showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword))}
            
            <button type="submit" disabled={loading} className="btn btn-primary w-full flex items-center justify-center gap-2">
              {loading ? <><Loader className="w-5 h-5 animate-spin" />Creating...</> : <><UserPlus className="w-5 h-5" />Create User</>}
            </button>
          </form>
        </div>
        
        {/* Bulk Upload Section */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Bulk User Upload
          </h2>
          
          <div className="space-y-4">
            {/* Download Template */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-600 mb-4">
                Download the Excel template, fill it with user data, and upload it here
              </p>
              <button
                onClick={handleDownloadTemplate}
                className="btn btn-secondary flex items-center gap-2 mx-auto"
              >
                <Download className="w-4 h-4" />
                Download Template
              </button>
            </div>
            
            {/* Upload File */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Excel File
              </label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                disabled={uploading}
                className="input w-full"
              />
              {uploadFile && (
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                  <FileSpreadsheet className="w-4 h-4" />
                  <span>{uploadFile.name}</span>
                  <button
                    onClick={() => {
                      setUploadFile(null);
                      setUploadResult(null);
                      setExcelErrors([]);
                      setErrorReportBlob(null);
                    }}
                    className="ml-auto text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            
            {/* Progress Indicator */}
            {uploading && (
              <div className="flex items-center gap-2 text-primary-600">
                <Loader className="w-5 h-5 animate-spin" />
                <span>Processing file...</span>
              </div>
            )}
            
            {/* Upload Results */}
            {uploadResult && (
              <div className={`p-4 rounded-lg ${uploadResult.failed > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`}>
                <div className="flex items-start gap-2">
                    {uploadResult.failed > 0 ? (
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${uploadResult.failed > 0 ? 'text-yellow-900' : 'text-green-900'}`}>
                      {uploadResult.failed > 0 
                        ? `${uploadResult.successful} user(s) created, ${uploadResult.failed} failed`
                        : `Successfully created ${uploadResult.successful} user(s)!`
                      }
                    </p>
                    {uploadResult.errors.length > 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        {uploadResult.errors.length} row(s) have errors
                      </p>
                    )}
                  </div>
                </div>
                
                {uploadResult.failed > 0 && errorReportBlob && (
                  <button
                    onClick={handleDownloadErrorReport}
                    className="btn btn-secondary mt-3 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Download Error Report
                  </button>
                )}
              </div>
            )}
            
            {/* Validation Errors */}
            {excelErrors.length > 0 && uploadResult === null && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-red-900">
                      Validation Failed
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {excelErrors.length} row(s) have validation errors. Please fix them before uploading.
                    </p>
                    {errorReportBlob && (
                      <button
                        onClick={handleDownloadErrorReport}
                        className="btn btn-secondary mt-3 flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download Error Report
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

